/**
 * API クライアント基盤
 * 経験則 #1: 一覧は {複数形:[...], total} を展開, 204 は空, パスはハイフン区切り複数形
 * 経験則 #10: 一覧取得はラッパー展開, 204 は空レスポンス扱い
 * 経験則 #50: ネットワークエラーは指数バックオフ最大3回リトライ（4xx はしない）
 */

import type { ApiError } from './types'

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? 'http://localhost:8787'

export class ApiClientError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
  ) {
    super(message)
    this.name = 'ApiClientError'
  }
}

// 指数バックオフ（最大 3 回リトライ, 5xx / ネットワークエラーのみ）
async function withRetry<T>(fn: () => Promise<T>, maxRetries = 3): Promise<T> {
  let lastError: unknown
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await fn()
    } catch (err) {
      lastError = err
      // 4xx はリトライしない
      if (err instanceof ApiClientError && err.status >= 400 && err.status < 500) {
        throw err
      }
      // ネットワークエラー or 5xx はリトライ
      if (attempt < maxRetries - 1) {
        await new Promise((r) => setTimeout(r, Math.min(1000 * 2 ** attempt, 8000)))
      }
    }
  }
  throw lastError
}

interface FetchOptions {
  method?: string
  body?: unknown
  signal?: AbortSignal
}

async function apiFetch<T>(path: string, options: FetchOptions = {}): Promise<T | null> {
  const { method = 'GET', body, signal } = options

  const headers: Record<string, string> = {}
  if (body !== undefined && body !== null) {
    headers['Content-Type'] = 'application/json'
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    method,
    headers,
    body: body !== undefined && body !== null ? JSON.stringify(body) : undefined,
    credentials: 'include', // session Cookie (sessionCookie scheme)
    signal,
  })

  // 204 No Content → null（削除・ログアウト等）
  if (response.status === 204) {
    return null
  }

  const text = await response.text()
  let json: unknown = null
  if (text) {
    try {
      json = JSON.parse(text)
    } catch {
      throw new ApiClientError(response.status, 'PARSE_ERROR', 'レスポンスの解析に失敗しました')
    }
  }

  if (!response.ok) {
    const errBody = json as Partial<ApiError>
    const code = errBody?.error?.code ?? 'UNKNOWN_ERROR'
    const message = errBody?.error?.message ?? `エラーが発生しました（${response.status}）`
    throw new ApiClientError(response.status, code, message)
  }

  return json as T
}

// 経験則 #50: 5xx / ネットワークエラーにリトライ。4xx はスルー。
export async function apiGet<T>(path: string, signal?: AbortSignal): Promise<T | null> {
  return withRetry(() => apiFetch<T>(path, { method: 'GET', signal }))
}

export async function apiPost<T>(path: string, body?: unknown, signal?: AbortSignal): Promise<T | null> {
  return withRetry(() => apiFetch<T>(path, { method: 'POST', body, signal }))
}

export async function apiPut<T>(path: string, body?: unknown, signal?: AbortSignal): Promise<T | null> {
  return withRetry(() => apiFetch<T>(path, { method: 'PUT', body, signal }))
}

export async function apiDelete(path: string, signal?: AbortSignal): Promise<null> {
  return withRetry(() => apiFetch<null>(path, { method: 'DELETE', signal }))
}
